import { CategoryDocument } from '@models/category';
import { ProductDocument } from 'models/product';
import { BreadcrumbItem } from '@ui/Breadcrumb';
import { getBreadcrumbItems } from '@util/index';
import { FAQItem } from '@util/maps/faq';
import { BlogDocument } from '@util/types/firestore/blog';
import { getProductSlug } from '@util/urlHelpers';
import { PublicUserDocument } from 'models/user';
import sanitizeHtml from 'sanitize-html';

export function injectProductScript(
  p: ProductDocument,
  schemaRatings: {
    product_rating: string;
    product_reviews: string;
  }
) {
  try {
    const sanitize = sanitizeHtml;
    const sanitizedSku = sanitize(p.sku ?? '');
    const sanitizedTitle = sanitize(p.title);
    const sanitizedDescription = sanitize(
      p.description ? p.description.replace(/\r?\n|\r/g, '') : p.category1 ?? ''
    );
    const sanitizedBrand = sanitize(
      p.category === 'Dirt Bikes' ? p.make ?? '' : p.brand ?? ''
    );
    const sanitizedUrl = sanitize(getProductSlug(p));
    const sanitizedImages = p.images
      .map((i) => [sanitize(i.full), sanitize(i.thumb)])
      .flat();
    const sanitizedShippingCost =
      p.shipping_costs && p.shipping_costs[0] && p.shipping_costs[0].cost
        ? sanitize(p.shipping_costs[0].cost.toFixed(2))
        : '0.00';

    // --- VARIATIONS HANDLING ---
    if (
      p.has_variations &&
      Array.isArray(p.variations) &&
      p.variations.length > 0
    ) {
      // Determine variesBy fields
      const variesBy: string[] = [];
      if (p.variations.some((v) => v.size !== undefined && v.size === 'N/A')) {
        variesBy.push('https://schema.org/size');
      }
      if (p.variations.some((v) => v.color !== undefined && v.color !== null)) {
        variesBy.push('https://schema.org/color');
      }
      // Add more variesBy fields as needed

      // ProductGroup object
      const productGroup: any = {
        '@context': 'https://schema.org/',
        '@type': 'ProductGroup',
        name: sanitizedTitle,
        description: sanitizedDescription,
        url: sanitizedUrl,
        brand: {
          '@type': 'Brand',
          name: sanitizedBrand,
        },
        productGroupID: p.id,
        variesBy,
        hasVariant: p.variations.map((v) => {
          // Compose variant name/description
          const variantName = [p.title, v.size, v.color]
            .filter(Boolean)
            .join(' ');
          const variantDescription = [p.description, v.size, v.color]
            .filter(Boolean)
            .join(' ');
          // Compose variant image
          let variantImage = v.image ? [sanitize(v.image)] : sanitizedImages;
          // Compose variant url (add query params for size/color)
          let variantUrl = sanitizedUrl;
          const params = [];
          if (v.size && v.size !== 'N/A')
            params.push(`size=${encodeURIComponent(v.size)}`);
          if (v.color) params.push(`color=${encodeURIComponent(v.color)}`);
          if (params.length) variantUrl += `?${params.join('&')}`;
          // Compose offer
          const offer: any = {
            '@type': 'Offer',
            url: variantUrl,
            priceCurrency: 'USD',
            price: v.price ? +v.price.toFixed(2) : 0,
            itemCondition:
              'https://schema.org/' +
              (p.condition === 'New' ? 'NewCondition' : 'UsedCondition'),
            availability:
              'https://schema.org/' +
              (v.qty && v.qty > 0 ? 'InStock' : 'OutOfStock'),
            shippingDetails: { '@id': '#shipping_policy' },
            hasMerchantReturnPolicy: { '@id': '#return_policy' },
          };
          return {
            '@type': 'Product',
            sku: v.sku || p.sku || p.id,
            image: variantImage,
            name: sanitize(variantName),
            description: sanitize(variantDescription),
            ...(v.color && { color: sanitize(v.color) }),
            ...(v.size && { size: sanitize(String(v.size)) }),
            offers: offer,
          };
        }),
      };
      // Add review/aggregateRating at ProductGroup level
      productGroup.review = {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: schemaRatings.product_rating,
          bestRating: '5',
        },
        author: {
          '@type': 'Person',
          name: 'MX Locker',
        },
      };
      productGroup.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: schemaRatings.product_rating,
        reviewCount: schemaRatings.product_reviews,
      };
      // Output as an array with ProductGroup, shipping, and return policy
      const shippingDetails = {
        '@context': 'https://schema.org/',
        '@type': 'OfferShippingDetails',
        '@id': '#shipping_policy',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: sanitizedShippingCost,
          currency: 'USD',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'US',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 5,
            maxValue: 10,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 5,
            maxValue: 10,
            unitCode: 'DAY',
          },
          cutOffTime: '19:30-08:00',
          businessDays: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              'https://schema.org/Monday',
              'https://schema.org/Tuesday',
              'https://schema.org/Wednesday',
              'https://schema.org/Thursday',
              'https://schema.org/Friday',
            ],
          },
        },
      };
      const returnPolicy = {
        '@context': 'http://schema.org/',
        '@type': 'MerchantReturnPolicy',
        '@id': '#return_policy',
        applicableCountry: 'US',
        returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
      };
      return JSON.stringify([productGroup, shippingDetails, returnPolicy]);
    }

    // --- NO VARIATIONS: fallback to single Product ---
    const obj = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      sku: p.id,
      ...(sanitizedSku && {
        mpn: sanitizedSku,
      }),
      name: sanitizedTitle,
      image: sanitizedImages,
      description: sanitizedDescription,
      brand: {
        '@type': 'Brand',
        name: sanitizedBrand,
      },
      review: {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: schemaRatings.product_rating,
          bestRating: '5',
        },
        author: {
          '@type': 'Person',
          name: 'MX Locker',
        },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: schemaRatings.product_rating,
        reviewCount: schemaRatings.product_reviews,
      },
      offers: {
        '@type': 'Offer',
        url: sanitizedUrl,
        itemCondition:
          'https://schema.org/' +
          (p.condition === 'New' ? 'NewCondition' : 'UsedCondition'),
        availability:
          'https://schema.org/' + (p.out_of_stock ? 'OutOfStock' : 'InStock'),
        priceValidUntil: new Date(
          new Date().setFullYear(new Date().getFullYear() + 1)
        )
          .toISOString()
          .split('T')[0],
        priceSpecification: {
          price: p.price ? +p.price.toFixed(2) : 0,
          priceCurrency: 'USD',
          ...(p.shipping_costs?.[0]?.code === 'US' && {
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: {
                '@type': 'MonetaryAmount',
                value: sanitizedShippingCost,
                currency: 'USD',
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'US',
              },
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: {
                  '@type': 'QuantitativeValue',
                  minValue: 5,
                  maxValue: 10,
                  unitCode: 'DAY',
                },
                transitTime: {
                  '@type': 'QuantitativeValue',
                  minValue: 5,
                  maxValue: 10,
                  unitCode: 'DAY',
                },
                cutOffTime: '19:30-08:00',
                businessDays: {
                  '@type': 'OpeningHoursSpecification',
                  dayOfWeek: [
                    'https://schema.org/Monday',
                    'https://schema.org/Tuesday',
                    'https://schema.org/Wednesday',
                    'https://schema.org/Thursday',
                    'https://schema.org/Friday',
                  ],
                },
              },
            },
          }),
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'US',
          returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
        },
      },
    };
    return JSON.stringify(obj);
  } catch (e) {
    console.error(e);
  }
}

export function injectBikeProductScript(p: ProductDocument) {
  try {
    const sanitizedTitle = sanitizeHtml(p.title);
    const sanitizedDescription = sanitizeHtml(
      p.description ? p.description.replace(/\r?\n|\r/g, '') : ''
    );
    const sanitizedSku = sanitizeHtml(p.sku ?? '');
    const sanitizedBrand = sanitizeHtml(p.make ?? '');
    const sanitizedUrl = sanitizeHtml(getProductSlug(p));

    const sanitizedImages = p.images
      .map((i) => [sanitizeHtml(i.full), sanitizeHtml(i.thumb)])
      .flat();

    let region = '';
    if (p.location_display) {
      const parts = p.location_display.split(',');
      if (parts.length > 1) {
        region = parts[1].trim().split(' ')[0];
      }
    }

    const obj = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: sanitizedTitle,
      image: sanitizedImages,
      description: sanitizedDescription,
      sku: p.id,
      ...(sanitizedSku && { mpn: sanitizedSku }),
      brand: {
        '@type': 'Brand',
        name: sanitizedBrand,
      },
      offers: {
        '@type': 'Offer',
        url: sanitizedUrl,
        priceCurrency: 'USD',
        price: p.price ? p.price.toFixed(2) : '0.00',
        itemCondition:
          'https://schema.org/' +
          (p.condition === 'New' ? 'NewCondition' : 'UsedCondition'),
        availability:
          'https://schema.org/' + (p.out_of_stock ? 'OutOfStock' : 'InStock'),
        eligibleRegion: region
          ? {
              '@type': 'Place',
              name: region,
            }
          : undefined,
        availableDeliveryMethod: 'https://schema.org/OnSitePickup',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '0.00',
            currency: 'USD',
          },
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'US',
            ...(region && { addressRegion: region }),
          },
        },
      },
    };

    return JSON.stringify(obj);
  } catch (e) {
    console.error(e);
  }
}

export function injectVideoScript(b: BlogDocument) {
  if (!b.video) return;
  try {
    const cleanTitle = sanitizeHtml(b.title);
    const cleanThumbnail = sanitizeHtml(b.thumbnail);
    const headline = sanitizeHtml(b.title);
    const authorName = sanitizeHtml(b.author);
    const url = sanitizeHtml(b.video);
    const obj = {
      '@context': 'https://schema.org/',
      '@type': 'SocialMediaPosting',
      '@id': url,
      datePublished: new Date(b.created).toISOString(),
      author: {
        '@type': 'Organization',
        name: authorName,
        url: 'https://mxlocker.com',
      },
      image: cleanThumbnail,
      headline: headline,
      sharedContent: {
        '@type': 'Blog',
        headline: cleanTitle,
        url: url,
      },
    };
    return JSON.stringify(obj);
  } catch (e) {
    console.error(e);
  }
}

export function injectYoutubeVideoScript({
  url,
  name,
  description,
  thumbnailUrl,
  uploadDate,
  embedUrl,
}: {
  url: string;
  name: string;
  description: string;
  thumbnailUrl: string[];
  uploadDate: string;
  embedUrl: string;
}) {
  const obj = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    url,
    name,
    description,
    thumbnailUrl,
    uploadDate,
    embedUrl,
  };
  return JSON.stringify(obj);
}

export function injectArticleScript(b: BlogDocument) {
  try {
    const cleanTitle = sanitizeHtml(b.title);
    const cleanThumbnail = sanitizeHtml(b.thumbnail);
    const cleanFullImage = sanitizeHtml(b.full_image);
    const cleanAuthor = sanitizeHtml(b.author);
    const cleanDescription = sanitizeHtml(b.description);

    const obj = {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: cleanTitle,
      image: [cleanThumbnail, cleanFullImage],
      datePublished: new Date(b.created).toISOString(),
      dateModified: new Date(b.created).toISOString(), // Use this if there's no modified date, else use the modified date
      author: {
        '@type': 'Person',
        name: cleanAuthor,
        // You might want to add the author URL if available
      },
      description: cleanDescription,
    };
    return JSON.stringify(obj);
  } catch (e) {
    console.error(e);
  }
}

export function injectBreadcrumbScript({
  breadcrumbs,
  category,
}: {
  breadcrumbs?: BreadcrumbItem[];
  category?: CategoryDocument;
}) {
  try {
    const bcs: BreadcrumbItem[] = breadcrumbs?.length
      ? breadcrumbs
      : category
      ? getBreadcrumbItems(category)
      : [];

    const itemListElement = bcs.map((bc, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: sanitizeHtml(bc.label),
      ...(bc.href && { item: sanitizeHtml(`https://mxlocker.com${bc.href}`) }),
    }));

    const obj = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement,
    };

    return JSON.stringify(obj);
  } catch (e) {
    console.error(e);
  }
}

export function injectFAQScript(faqItems: FAQItem[]) {
  try {
    const mainEntity = faqItems.map((item) => ({
      '@type': 'Question',
      name: sanitizeHtml(item.q),
      acceptedAnswer: {
        '@type': 'Answer',
        text: sanitizeHtml(item.a.trim().replace(/\r?\n|\r/g, '')),
      },
    }));

    const obj = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity,
    };

    return JSON.stringify(obj);
  } catch (e) {
    console.error(e);
  }
}

interface WebSiteScript {
  url: string;
  searchUrlTemplate: string;
}

export function injectWebSiteScript({ url, searchUrlTemplate }: WebSiteScript) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'MX Locker',
    url: url,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: searchUrlTemplate,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return sanitizeHtml(JSON.stringify(structuredData));
}

interface SoftwareAppScript {
  name: string;
  operatingSystem: string;
  applicationCategory: string;
  ratingValue: string;
  ratingCount: string;
  price: string;
  priceCurrency: string;
}

export function injectSoftwareAppScript({
  name,
  operatingSystem,
  applicationCategory,
  ratingValue,
  ratingCount,
  price,
  priceCurrency,
}: SoftwareAppScript) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    operatingSystem,
    applicationCategory,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue,
      ratingCount,
    },
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency,
    },
  };

  return sanitizeHtml(JSON.stringify(structuredData));
}

export function injectLogoScript(url: string, logoUrl: string) {
  const obj = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: sanitizeHtml(url),
    logo: sanitizeHtml(logoUrl),
  };

  return JSON.stringify(obj);
}

export function injectProfileScript(user: PublicUserDocument) {
  const obj = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    dateCreated: new Date(user.created).toISOString(),
    dateModified: new Date(user.created).toISOString(),
    mainEntity: {
      '@type': 'Person',
      name: user.username,
      alternateName: user.username_slug,
      identifier: user.uid,
      interactionStatistic: [
        {
          '@type': 'InteractionCounter',
          interactionType: 'https://schema.org/FollowAction',
          userInteractionCount: user.followers,
        },
      ],
      description: user.bio,
      image: user.photo,
      sameAs: [`https://mxlocker.com/profile/${user.username_slug}`],
    },
  };

  return JSON.stringify(obj);
}
